/**
 * LES PRESTATIONS EKIVOX D'UN CLIENT (DEC-026, DEC-027).
 *
 * Une intervention s'impute à une PRESTATION Ekivox (ex. AIDE0305
 * « Intervention de l'équipe IT Solutions »), choisie dans une liste
 * PRÉFILTRÉE sur le client : un client de la borne et un dossier Ekivox sont
 * le même client quand ils ont le même code dossier (`core.client.numero_client`
 * = numéro de dossier Ekivox, DEC-013). Les prestations se LISENT dans le
 * miroir `ekivox` (service ekivox-sync), jamais recopiées (règle 5).
 *
 * L'intervention n'enregistre que la CLÉ de la prestation (DEC-053) : le code
 * dossier du client et le code de la prestation (AIDE0305…). Le libellé, et
 * surtout la MISSION qui la porte (ID_MISSION), se cherchent dans le miroir —
 * la mission à l'export seulement, pour le jour de l'intervention
 * (`prestationDeLaCle`) : un avenant ou une nouvelle version de la lettre de
 * mission ne laisse plus la ligne accrochée à une prestation périmée.
 */
/**
 * Le libellé de la typologie, et ce qu'il faut dire quand on ne sait pas : un
 * « non » affiché à tort ferait croire qu'un client n'a pas de contrat.
 */
export function libelleClientIts(v) {
    if (v === null)
        return 'Typologie Ekivox inconnue';
    return v ? 'Client IT Solutions' : 'Pas client IT Solutions';
}
/**
 * La société du groupe qui porte les prestations de la borne : IT Solutions,
 * code `04` dans Ekivox (projets DIGITAL), relevé le 05/10/2026. Un même
 * client a aussi des projets comptables ou juridiques d'autres sociétés du
 * groupe (`02`, `03`) : la borne n'en propose que les ARTICLES qu'IT
 * Solutions réalise (`ekivox.v_article`, DEC-039 — « SAV sur les outils
 * Altonéo », « Audit de cybersécurité »…), et l'API refuse le reste.
 */
export const SOCIETE_EKIVOX_ITS = '04';
/**
 * LA RÉVISION REVI0101 SE PROPOSE AUSSI (DEC-052), bien qu'elle ne soit pas un
 * article d'IT Solutions : présente dans une mission comptable (société 02) du
 * client, elle s'impute comme ses DIGT…, avec les mêmes règles (DEC-049,
 * DEC-051). REVI0101 seulement — la révision des EI / sociétés IR en BNC :
 * les autres révisions principales (REVI0201, 0301, 0501…) et les sous-lignes
 * REVI…X (liasse, FEC, DAS2…) restent hors de la borne, choix de C. Hayer.
 */
export const SOCIETE_EKIVOX_EC = '02';
export const PRESTATIONS_EC_PROPOSEES = ['REVI0101'];
export const LIBELLES_CATEGORIE_IMPUTATION = {
    its: 'Mission ITS',
    ec: 'Mission EC',
    projet: 'Projets',
};
export function categoriePrestation(p) {
    return p.societe === SOCIETE_EKIVOX_EC ? 'ec' : 'its';
}
/**
 * Statuts Ekivox qui ferment, relevés sur la pré-production le 05/10/2026.
 * Projet : A_FAIRE, EN_COURS, FAIT, ANNULE. Prestation : A_FAIRE, EN_COURS,
 * TERMINEE, NON_APPLICABLE, ANNULEE_AVENANT. « À faire » est ouvert : on peut
 * déjà y imputer du temps.
 */
export const STATUTS_PROJET_CLOS = ['FAIT', 'ANNULE'];
export const STATUTS_PRESTATION_CLOS = ['TERMINEE', 'NON_APPLICABLE', 'ANNULEE_AVENANT'];
/** Pourquoi une prestation n'est plus proposée ; null si elle l'est. */
function fermeture(p, jour) {
    if (p.supprimee)
        return 'retirée d’Ekivox';
    if (p.statut && STATUTS_PRESTATION_CLOS.includes(p.statut))
        return p.statut === 'TERMINEE' ? 'terminée' : 'non applicable';
    if (p.projetStatut && STATUTS_PROJET_CLOS.includes(p.projetStatut))
        return 'mission close';
    if ((p.dateDebut && p.dateDebut > jour) || (p.dateFin && p.dateFin < jour))
        return 'hors période';
    return null;
}
/**
 * Une prestation EN COURS le jour de l'intervention : présente chez Ekivox,
 * ni elle ni sa mission closes, et la mission couvre ce jour (une date absente
 * ne borne pas). Un statut inconnu ne l'écarte pas : une prestation ouverte
 * cachée par erreur serait pire qu'une prestation close proposée.
 */
export function prestationEnCours(p, jour) {
    return fermeture(p, jour) === null;
}
/**
 * Les prestations à proposer pour une intervention du jour `jour` : celles en
 * cours, plus celles du code déjà choisi (`actuel`) même si elles ne le sont
 * plus — corriger une intervention ne doit pas effacer sa prestation en silence.
 */
export function prestationsProposees(prestations, jour, actuel) {
    return prestations
        .filter((p) => prestationEnCours(p, jour) || (!!actuel && p.code === actuel && !p.supprimee))
        .sort((a, b) => (b.dateDebut ?? '').localeCompare(a.dateDebut ?? '') ||
        (a.projet ?? '').localeCompare(b.projet ?? '') ||
        (a.code ?? '').localeCompare(b.code ?? ''));
}
/**
 * Le libellé d'une prestation dans une liste : code et libellé. La mission
 * n'y figure plus (DEC-053) : on choisit un CODE, et la mission se cherche à
 * l'export — deux « AIDE0305 » du même dossier ne font qu'un choix.
 */
export function libellePrestation(p, jour) {
    const nom = [p.code, p.libelle?.trim()].filter(Boolean).join(' — ') || `Prestation ${p.id}`;
    const pourquoi = fermeture(p, jour);
    return `${nom}${pourquoi ? ` (${pourquoi})` : ''}`;
}
/**
 * LA PRESTATION QUE DÉSIGNE UNE CLÉ (DEC-053) — code dossier + code prestation
 * — le jour d'une intervention : celle dont la MISSION part dans ID_MISSION à
 * l'export. `candidates` : les prestations du dossier que la borne propose
 * (périmètre IT Solutions, DEC-049, DEC-051 — la base les filtre déjà).
 *
 *  1. parmi celles du code, présentes chez Ekivox, celles EN COURS ce jour-là ;
 *  2. à défaut, celles dont la mission COUVRE ce jour, même close depuis : le
 *     temps d'un mois passé se facture sur la mission de ce mois-là ;
 *  3. à égalité, la plus récente — début le plus tardif, puis la version de
 *     la lettre (`versionMission`), puis le nom et l'identifiant de la mission,
 *     pour qu'il en reste toujours exactement une.
 *
 * Null : aucune mission du dossier ne porte ce code à cette date — la ligne
 * ne part pas, la pré-facturation dit pourquoi (`manquesImputation`).
 * Écarté : retomber sur la mission la plus récente hors de sa période — le
 * temps de 2025 partirait sur la lettre de 2026.
 */
export function prestationDeLaCle(candidates, code, jour) {
    const siennes = candidates.filter((p) => p.code === code && !p.supprimee);
    const couvre = (p) => !(p.dateDebut && p.dateDebut > jour) && !(p.dateFin && p.dateFin < jour);
    return [...siennes.filter((p) => prestationEnCours(p, jour))].sort(plusRecente)[0] ?? [...siennes.filter(couvre)].sort(plusRecente)[0] ?? null;
}
/**
 * Ce que l'écran propose (DEC-053) : UN choix par code, représenté par la
 * prestation que la clé désignerait ce jour-là — c'est elle qui donne le
 * libellé et la famille (DEC-052). `proposees` : `prestationsProposees`.
 */
export function prestationsParCode(proposees, jour) {
    const codes = [...new Set(proposees.map((p) => p.code).filter((c) => !!c))];
    return codes
        .map((c) => prestationDeLaCle(proposees, c, jour) ?? proposees.find((p) => p.code === c))
        .sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''));
}
/** Refus d'une prestation étrangère au client (règle 9 : dire quoi corriger). */
export function refusPrestationEkivox(client) {
    return client
        ? `Cette prestation Ekivox n’appartient pas à ${client} : choisissez une prestation dans la liste du client.`
        : 'Une intervention sans client (temps interne) ne s’impute à aucune prestation Ekivox.';
}
/**
 * QUAND LE CLIENT N'A PAS DE MISSION (DEC-043).
 *
 * Un client sans aucune prestation IT Solutions en cours dans Ekivox : le temps
 * passé chez lui ne peut s'imputer nulle part. L'intervenant DEMANDE alors la
 * création d'une mission portant la prestation d'assistance, AIDE0305 — un
 * courriel aux gestionnaires des missions Ekivox, depuis le champ prestation —
 * et met son intervention EN ATTENTE D'AFFECTATION. Il revient l'y rattacher
 * quand la mission apparaît dans le miroir (synchronisation toutes les 15 min).
 *
 * L'attente n'est pas un état saisi : elle se LIT — marque posée par
 * l'intervenant ET aucune prestation sur l'intervention (règle 4). Choisir la
 * prestation suffit à en sortir.
 *
 * Écarté : créer la mission depuis la borne — jamais d'écriture dans Ekivox
 * par l'API (DEC-033) ; c'est un geste d'Ekivox, fait par ceux qui le tiennent.
 */
export const PRESTATION_ASSISTANCE = { code: 'AIDE0305', libelle: 'Intervention de l’équipe IT Solutions' };
/**
 * LES ENTITÉS DU GROUPE DEMANDENT AIDE0306, PAS AIDE0305 (DEC-054) : AIDE0305
 * est la prestation d'assistance des CLIENTS ; celle du groupe — toutes les
 * sociétés « ALTONEO … », « IT Solutions … » et « Acceda » — est AIDE0306
 * (C. Hayer, 30/10/2026). Libellé encore inconnu : aucune AIDE0306 dans le
 * miroir au 30/10/2026.
 */
export const PRESTATION_ASSISTANCE_GROUPE = { code: 'AIDE0306', libelle: null };
/**
 * Une société du groupe, reconnue à sa RAISON SOCIALE — la définition qu'en
 * donne la direction. Le début du nom seulement : « CSE ALTONEO CONSEIL » et
 * « WEEKEND ALTONEO » (relevés en PROD le 30/10/2026) n'en sont pas.
 *
 * Écarté : le drapeau `interne` de `core.client`, qu'Ekivox n'écrit pas et que
 * la borne n'a posé qu'à la fusion des doublons (21/10) — ACCEDA, ALTONEO
 * CONSEIL (02), ALTONEO AUDIT (03), ALTONEO AUDIT LE MANS et les ALTONEO IMMO
 * y sont à faux. La raison sociale a été écartée pour les demandes internes
 * (`demandeInterne`), où une erreur range mal des milliers de demandes ; ici,
 * elle ne change que le code écrit dans un courriel que relit un gestionnaire.
 */
const NOM_DU_GROUPE = /^\s*(altoneo|altonéo|it\s+solutions|acceda)\b/i;
export function entiteDuGroupe(raisonSociale) {
    return NOM_DU_GROUPE.test(raisonSociale ?? '');
}
/** La prestation d'assistance à demander pour ce client : AIDE0306 pour le groupe, AIDE0305 sinon. */
export function prestationAssistance(raisonSociale) {
    return entiteDuGroupe(raisonSociale) ? PRESTATION_ASSISTANCE_GROUPE : PRESTATION_ASSISTANCE;
}
/** « AIDE0305 « Intervention… » », ou le code seul quand le libellé manque. */
export function nomPrestationAssistance(p) {
    return p.libelle ? `${p.code} « ${p.libelle} »` : p.code;
}
/**
 * LA FICHE D'UN DOSSIER DANS LE CRM EKIVOX, par l'identifiant Ekivox du client
 * (`core.client.ekivox_id`, posé par la synchro des clients). Une seule adresse
 * pour les deux VM : STAGING lit aussi l'Ekivox de production.
 */
export const EKIVOX_CRM_URL = 'https://crm.ekivox.fr/altoneo';
export const lienEkivoxDossier = (ekivoxId) => `${EKIVOX_CRM_URL}#/dossiers/${encodeURIComponent(ekivoxId)}`;
/**
 * LES PRESTATIONS DIGITALES D'UNE LETTRE DE MISSION SANS RÉVISION (DIGT…) NE
 * SE PROPOSENT PAS. Un même dossier comptable a souvent plusieurs lettres de
 * mission pour un exercice, et chacune reprend les mêmes DIGT0102, DIGT0104…
 * (dépôt et facturation Inqom, synchro d'outils tiers) : l'intervenant voyait
 * la même prestation en double ou en triple. Seule la mission qui porte aussi
 * la révision des comptes (REVI…) est la vraie : ses DIGT sont les seules
 * proposées, et les seules que l'API accepte.
 *
 * La révision se cherche dans la MÊME mission (projet Ekivox), pas dans le
 * dossier : une REVI dans la mission d'à côté ne rend pas les DIGT de celle-ci
 * légitimes — ce serait garder les doublons. Une REVI retirée d'Ekivox compte
 * encore, comme toute prestation retirée : l'historique en porte.
 *
 * Écarté : filtrer dans la synchro (dépôt Ekivox-Integration) — le miroir doit
 * rester fidèle à Ekivox, la pré-facturation relit les DIGT déjà imputées.
 */
export const PREFIXE_PRESTATION_DIGITALE = 'DIGT';
export const PREFIXE_PRESTATION_REVISION = 'REVI';
/** Le `AAAA_MM` du nom d'une mission Ekivox, '' s'il n'en porte pas. */
export function versionMission(projet) {
    return /_(\d{4}_\d{2})_/.exec(projet ?? '')?.[1] ?? '';
}
const estRevision = (p) => !!p.code?.startsWith(PREFIXE_PRESTATION_REVISION);
const cleMission = (p) => `${versionMission(p.projet)}\u0000${p.projet ?? ''}\u0000${p.projetId}`;
/** La plus récente d'abord : début le plus tardif, puis `cleMission`, comparée par points de code comme `collate "C"`. */
function plusRecente(a, b) {
    const ka = cleMission(a);
    const kb = cleMission(b);
    return (b.dateDebut ?? '').localeCompare(a.dateDebut ?? '') || (ka < kb ? 1 : ka > kb ? -1 : 0);
}
/**
 * La mission plus récente qui remplace celle de `prestation` sur le même
 * exercice (son nom), ou null. `dossier` : les prestations du même dossier.
 */
export function missionRemplacante(prestation, dossier) {
    const revisions = dossier.filter(estRevision);
    let plusRecente = null;
    for (const r0 of revisions.filter((r) => r.projetId === prestation.projetId)) {
        for (const r1 of revisions) {
            const memeExercice = r1.dateDebut === r0.dateDebut && r1.dateFin === r0.dateFin && (r1.societe ?? null) === (r0.societe ?? null);
            if (memeExercice && cleMission(r1) > cleMission(r0) && (!plusRecente || cleMission(r1) > cleMission(plusRecente)))
                plusRecente = r1;
        }
    }
    return plusRecente ? (plusRecente.projet ?? plusRecente.projetId) : null;
}
/** `prestation` peut-elle être proposée, vu les prestations de son dossier (`dossier`, elle comprise) ? */
export function prestationProposable(prestation, dossier) {
    return refusPrestationProposable(prestation, dossier) === null;
}
/** Le refus d'une prestation que la borne ne propose pas (DEC-049, DEC-051), ou null. */
export function refusPrestationProposable(prestation, dossier) {
    const mission = dossier.filter((p) => p.projetId === prestation.projetId);
    if (prestation.code?.startsWith(PREFIXE_PRESTATION_DIGITALE) && !mission.some(estRevision))
        return REFUS_PRESTATION_SANS_REVISION;
    const remplacante = missionRemplacante(prestation, dossier);
    return remplacante ? refusMissionRemplacee(remplacante) : null;
}
/** Refus d'une prestation DIGT… d'une mission sans révision : ce qui bloque, et quoi choisir. */
export const REFUS_PRESTATION_SANS_REVISION = `Cette prestation ${PREFIXE_PRESTATION_DIGITALE}… appartient à une lettre de mission sans révision des comptes ` +
    `(${PREFIXE_PRESTATION_REVISION}…) : choisissez la même prestation dans la mission qui porte la révision.`;
/** Refus d'une prestation d'une version périmée de la lettre de mission : laquelle choisir. */
export function refusMissionRemplacee(remplacante) {
    return `Cette prestation appartient à une version périmée de la lettre de mission : ` +
        `choisissez la même prestation dans la mission ${remplacante}, la plus récente sur cet exercice.`;
}
/** Ce que l'intervenant doit faire ensuite : dit à la saisie, et rappelé dans sa feuille de temps. */
export const RAPPEL_ATTENTE_MISSION = 'Revenez rattacher cette intervention à la mission du client dès qu’elle apparaîtra dans Ekivox : ' +
    'elle vous attend dans votre feuille de temps, sous « En attente d’une mission ».';
const HEURE_PARIS = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' });
const JOUR_PARIS = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' });
/**
 * Peut-on demander une mission pour ce client ? Rend le refus, ou null
 * (règle 9). Une demande DÉJÀ PARTIE le même jour pour le même client suffit :
 * sans ce refus, trois intervenants chez le même client relançaient trois fois
 * les mêmes personnes. Une demande qui n'a prévenu personne (messagerie non
 * branchée, garde-fou de recette) ne compte pas : on peut la refaire.
 */
export function refusDemandeMission(p) {
    if (p.itSolutions)
        return 'IT Solutions elle-même : son temps est interne et ne s’impute à aucune mission Ekivox.';
    if (!p.configure) {
        return `Les prestations Ekivox ne sont pas branchées sur cette borne : impossible de vérifier que ${p.client} n’a pas déjà sa mission. Demandez-la directement aux gestionnaires des missions Ekivox.`;
    }
    if (p.enCours > 0)
        return `${p.client} a déjà une prestation Ekivox en cours ce jour-là : choisissez-la dans la liste, aucune mission n’est à créer.`;
    if (!p.destinataires) {
        return 'Aucun destinataire n’est configuré pour les demandes de mission sur cette borne (DESTINATAIRES_DEMANDE_MISSION) : prévenez un administrateur.';
    }
    const d = p.derniere;
    if (d && d.courriel === 'envoye' && JOUR_PARIS.format(new Date(d.le)) === JOUR_PARIS.format(p.maintenant)) {
        return (`La création d’une mission ${p.code} pour ${p.client} a déjà été demandée aujourd’hui à ${HEURE_PARIS.format(new Date(d.le))}` +
            `${d.par ? ` par ${d.par}` : ''}. Mettez l’intervention en attente : vous la rattacherez dès que la mission apparaîtra.`);
    }
    return null;
}
/**
 * L'ATTENTE SE POSE D'ELLE-MÊME (DEC-048) : un temps client sans prestation
 * Ekivox attend son affectation — que le client ait ou non une prestation en
 * cours ce jour-là. Plus aucun refus pour prestation manquante : l'intervenant
 * saisit, et sa feuille de temps lui rappelle de rattacher la ligne (« mission
 * arrivée » dès qu'il y a une prestation à choisir). Écarté (DEC-033 puis
 * DEC-045) : exiger la prestation quand le client en a une — cela bloquait la
 * saisie sur un ticket pour un détail que la feuille de temps rattrape.
 *
 * Rend ce qu'il faut poser : `true` (en attente), `false` (pas d'attente : du
 * temps interne, une prestation choisie, ou rien à imputer — zéro minute
 * facturable), ou `null` quand il n'y a rien à décider — le miroir manque
 * (règle 7), ou une ligne de l'historique qui n'a jamais eu ni prestation ni
 * attente et qu'on ne force pas (l'historique repris ne porte qu'une mission).
 * Dans ces deux cas, ce que dit la saisie (`attenteMission`) fait foi.
 *
 * UN TEMPS POSÉ SUR UN PROJET PROJECT'IT N'ATTEND JAMAIS (0114, 27/10/2026) :
 * le projet porte sa prestation, ou ne la porte pas encore — et le temps est
 * alors libre, sans rappel. C'est le verrou que la Direction a fait ôter : les
 * projets remontent dans la borne avant d'avoir mission et code, et l'on ne
 * doit pas punir celui qui saisit pour une affectation qui ne dépend pas de lui.
 */
export function attenteAutomatique(p) {
    if (!p.configure)
        return null;
    if (p.projet)
        return false;
    if (p.avant && !p.avant.prestation && !p.avant.attente)
        return null;
    if (p.usage !== 'client' || p.prestation)
        return false;
    return p.minutes > 0;
}
/**
 * LA PRESTATION QU'IMPUTE UN PROJET PROJECT'IT (0114) : parmi les prestations
 * du client, celle de la mission et du code que porte le projet — de
 * préférence en cours le jour de l'intervention, comme la liste les propose ;
 * à défaut la plus récente (une correction tardive vise souvent une prestation
 * close depuis). Null quand le projet n'en porte pas, ou qu'Ekivox ne la
 * connaît pas (encore) : le temps reste alors libre.
 *
 * La ligne n'en garde que le CODE (DEC-053) : la mission du projet sert à
 * vérifier qu'Ekivox porte bien ce code au client ; l'export cherche la sienne.
 */
export function prestationDuProjet(prestations, projet, jour) {
    if (!projet.missionEkivox || !projet.codeEkivox)
        return null;
    const siennes = prestations.filter((p) => !p.supprimee && p.projetId === projet.missionEkivox && p.code === projet.codeEkivox);
    return prestationsProposees(siennes, jour)[0] ?? siennes[0] ?? null;
}
/** Le projet porte-t-il cette prestation ? Elle ne se propose alors plus à part : on la choisit par le projet. */
export function prestationPorteeParProjet(p, projets) {
    return projets.some((x) => x.missionEkivox === p.projetId && x.codeEkivox === p.code);
}
//# sourceMappingURL=ekivox.js.map